// Global color persistence utility to handle auth state changes
// This ensures colors persist even when components re-mount

interface ColorState {
  customColors: {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
  };
  brandingType: 'beyondpages' | 'custom' | 'none';
  colorMode: 'gradient' | 'structured';
  lastUpdated: number;
}

const STORAGE_KEY = 'qr_form_persistent_state';
const EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes

class ColorPersistenceManager {
  private state: ColorState | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as ColorState;
        // Check if state is not too old
        if (Date.now() - parsed.lastUpdated < EXPIRY_TIME) {
          this.state = parsed;
          console.log('🎨 ColorPersistence - Loaded state from localStorage:', this.state);
        } else {
          console.log('🎨 ColorPersistence - State expired, clearing');
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error('🎨 ColorPersistence - Failed to load state:', e);
    }
  }

  saveState(partial: Partial<Omit<ColorState, 'lastUpdated'>>): void {
    this.state = {
      ...(this.state || {
        customColors: {
          primaryColor: '#3B82F6',
          secondaryColor: '#1F2937',
          textColor: '#FFFFFF'
        },
        brandingType: 'beyondpages',
        colorMode: 'structured'
      }),
      ...partial,
      lastUpdated: Date.now()
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      console.log('🎨 ColorPersistence - Saved state:', this.state);
    } catch (e) {
      console.error('🎨 ColorPersistence - Failed to save state:', e);
    }
  }

  getState(): ColorState | null {
    return this.state;
  }

  clearState(): void {
    this.state = null;
    localStorage.removeItem(STORAGE_KEY);
    console.log('🎨 ColorPersistence - Cleared state');
  }

  updateColors(colors: ColorState['customColors']): void {
    this.saveState({ customColors: colors });
  }

  updateBrandingType(type: ColorState['brandingType']): void {
    this.saveState({ brandingType: type });
  }

  updateColorMode(mode: ColorState['colorMode']): void {
    this.saveState({ colorMode: mode });
  }
}

// Create a singleton instance
export const colorPersistence = new ColorPersistenceManager();